import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './MeetingSpacePopup.css';

const MeetingSpacePopup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showAddSpaceModal, setShowAddSpaceModal] = useState(false);
  const [showNoSpaceModal, setShowNoSpaceModal] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);

  // Initialize component mount state and check authentication
  useEffect(() => {
    setIsMounted(true);
    
    // Check if user is authenticated
    const checkAuth = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        
        if (storedUser && storedToken) {
          const parsedUser = JSON.parse(storedUser);
          const currentUserId = parsedUser._id || parsedUser.id;
          setIsAuthenticated(true);
          setUserId(currentUserId);
        } else {
          setIsAuthenticated(false);
          setUserId(null);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setIsAuthenticated(false);
        setUserId(null);
      }
    };

    checkAuth();

    // Listen for storage changes (login/logout)
    const handleStorage = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', checkAuth);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', checkAuth);
    };
  }, []);

  // Check and show popup only on /meeting-home route and when authenticated
  useEffect(() => {
    // Wait for component to mount
    if (!isMounted) {
      return;
    }

    // Only show on meeting-home route
    if (location.pathname !== '/meeting-home') {
      return;
    }

    // Only show if user is authenticated
    if (!isAuthenticated || !userId) {
      console.log('MeetingSpacePopup: User not authenticated, skipping');
      return;
    }

    // Check if popup has been shown for this user
    const popupKey = `meetingHomePopupShown_${userId}`;
    const hasSeenPopup = localStorage.getItem(popupKey);
    
    // Debug log
    console.log('MeetingSpacePopup: Checking first-time visit', {
      hasSeenPopup,
      userId,
      pathname: location.pathname,
      isAuthenticated,
      isMounted
    });
    
    // Show popup if not seen before for this user
    if (!hasSeenPopup || hasSeenPopup !== 'true') {
      // Show popup after a short delay to ensure page is loaded
      const timer = setTimeout(() => {
        console.log('MeetingSpacePopup: Showing question modal');
        setShowQuestionModal(true);
      }, 1000);
      
      return () => {
        clearTimeout(timer);
      };
    } else {
      console.log('MeetingSpacePopup: User has already seen popup, skipping');
    }
  }, [isMounted, location.pathname, isAuthenticated, userId]);

  // Disable body scroll when any modal is open
  useEffect(() => {
    if (showQuestionModal || showAddSpaceModal || showNoSpaceModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showQuestionModal, showAddSpaceModal, showNoSpaceModal]);

  const handleYes = () => {
    setShowQuestionModal(false);
    setShowAddSpaceModal(true);
  };

  const handleNo = () => {
    setShowQuestionModal(false);
    setShowNoSpaceModal(true);
  };

  const markPopupAsShown = () => {
    if (userId) {
      const popupKey = `meetingHomePopupShown_${userId}`;
      localStorage.setItem(popupKey, 'true');
    }
  };

  const handleAddRoom = () => {
    markPopupAsShown();
    setShowAddSpaceModal(false);
    navigate('/meetings/add-your-space');
  };

  const handleViewRooms = () => {
    markPopupAsShown();
    setShowNoSpaceModal(false);
    navigate('/meetings/search');
  };

  const handleNotNow = () => {
    markPopupAsShown();
    setShowAddSpaceModal(false);
    setShowNoSpaceModal(false);
  };

  const handleCloseQuestion = () => {
    markPopupAsShown();
    setShowQuestionModal(false);
    console.log('MeetingSpacePopup: Question modal closed');
  };

  // Don't render anything if not on correct route or not authenticated
  if (location.pathname !== '/meeting-home' || !isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Question Modal */}
      {showQuestionModal && (
        <div 
          className="meeting-popup-overlay" 
          onClick={handleCloseQuestion}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '1rem'
          }}
        >
          <div 
            className="meeting-popup-container" 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              backgroundColor: 'white',
              borderRadius: '1.5rem',
              maxWidth: '500px',
              width: '100%',
              zIndex: 100000
            }}
          >
            <button
              className="meeting-popup-close"
              onClick={handleCloseQuestion}
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="meeting-popup-content">
              <h2 className="meeting-popup-title">
                Do you have a meeting space in your office?
              </h2>

              <div className="meeting-popup-buttons">
                <button
                  className="meeting-popup-btn meeting-popup-btn-primary"
                  onClick={handleYes}
                >
                  Yes
                </button>
                <button
                  className="meeting-popup-btn meeting-popup-btn-secondary"
                  onClick={handleNo}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Your Space Modal */}
      {showAddSpaceModal && (
        <div className="meeting-popup-overlay" onClick={handleNotNow}>
          <div className="meeting-popup-container" onClick={(e) => e.stopPropagation()}>
            <button
              className="meeting-popup-close"
              onClick={handleNotNow}
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="meeting-popup-content">
              <h2 className="meeting-popup-title">Add Your Space</h2>
              <p className="meeting-popup-text">
                Add your meeting room once and manage bookings easily.
              </p>

              <div className="meeting-popup-buttons">
                <button
                  className="meeting-popup-btn meeting-popup-btn-primary"
                  onClick={handleAddRoom}
                >
                  Add Room
                </button>
                <button
                  className="meeting-popup-btn meeting-popup-btn-secondary"
                  onClick={handleNotNow}
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Space Modal */}
      {showNoSpaceModal && (
        <div className="meeting-popup-overlay" onClick={handleNotNow}>
          <div className="meeting-popup-container" onClick={(e) => e.stopPropagation()}>
            <button
              className="meeting-popup-close"
              onClick={handleNotNow}
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="meeting-popup-content">
              <p className="meeting-popup-text">
                Book comfortable and professional meeting rooms near you.
              </p>

              <div className="meeting-popup-buttons">
                <button
                  className="meeting-popup-btn meeting-popup-btn-primary"
                  onClick={handleViewRooms}
                >
                  View Rooms
                </button>
                <button
                  className="meeting-popup-btn meeting-popup-btn-secondary"
                  onClick={handleNotNow}
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MeetingSpacePopup;

